import React, { useState, useEffect, useRef } from 'react';
import './App.css';

// Import Core Components
import MainPage from './components/Core/MainPage';
import SupabaseAuth from './components/Core/SupabaseAuth';
import CreditsPanel from './components/Core/CreditsPanel';
import AuthSim from './components/Core/AuthSim';

// Import App Store Modules
import EnhancedAppStore from './components/AppStore/EnhancedAppStore';
import AppDetails from './components/AppStore/AppDetails';
import DeveloperPortal from './components/AppStore/DeveloperPortal';
import AdminReviewSystem from './components/AppStore/AdminReviewSystem';

// Import Billing Module
import BillingDashboard from './components/Billing/BillingDashboard';

// Import Developer Modules
import APIKeysModule from './components/Developer/APIKeysModule';
import APIPlaygroundModule from './components/Developer/APIPlaygroundModule';
import AppAnalyticsModule from './components/Developer/AppAnalyticsModule';

// Keep some reference/testing modules (optional - can be removed later)
import ENoticeModule from './components/eNotice';
import LibraryModule from './components/Library';
import GroupMessagesModule from './components/GroupMessages/GroupMessagesModule';

// Import helper functions
import { normalizeRole, isDeveloperRole, isAdminRole } from './lib/utils/roleUtils';
import { a2aDeveloperService } from './a2a/services/a2aDeveloperService.js';
import { getAdminDeveloperProfile } from './services/adminDeveloperService.js';

// Import Orchestra AI Platform components
import {
  OrchestraWorkspace,
  OrchestraScenarios,
  OrchestraWorkflowBuilder,
  OrchestraToolbox,
} from './components/OrchestraAI';

function App() {
  // Main application state
  const [activeTab, setActiveTab] = useState('main');
  const [previousTab, setPreviousTab] = useState(null); // Track previous tab for back navigation
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // Sidebar starts hidden by default
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [selectedAppId, setSelectedAppId] = useState(null); // State for selected app in AppDetails
  const [workflowId, setWorkflowId] = useState(null); // State for workflow ID when navigating to workflow builder
  const lastToggleTime = useRef(0); // Track last toggle time to prevent rapid clicking

  // Initialize component on mount
  useEffect(() => {
    // Simulate authentication check
    setTimeout(() => {
      setIsAuthenticated(false); // Start with false to show login
      setIsLoading(false);
    }, 100);

    // Handle window resize for responsive sidebar behavior
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      
      // Always hide sidebar on mobile, but don't auto-show on desktop
      if (mobile) {
        setIsSidebarOpen(false);
      }
      // Desktop: keep current sidebar state (don't auto-show)
    };

    // Set initial sidebar state based on screen size
    handleResize();

    // Add event listener for window resize
    window.addEventListener('resize', handleResize);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Handle sidebar toggle with proper event handling
  const handleSidebarToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Prevent rapid clicking by checking time between clicks
    const now = Date.now();
    if (now - lastToggleTime.current < 300) { // 300ms debounce
      return;
    }
    lastToggleTime.current = now;
    
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Handle tab changes
  const handleTabChange = (tabName, sourceInfo = null) => {
    console.log('App: Tab change requested:', tabName, 'with source:', sourceInfo);
    
    // Always hide sidebar after navigation (both mobile and desktop)
    setIsSidebarOpen(false);
    
    // Save current tab as previous tab before changing (unless it's the same tab)
    if (activeTab !== tabName) {
      setPreviousTab(activeTab);
    }
    
    // Extract workflowId from sourceInfo if provided
    if (sourceInfo && sourceInfo.workflowId) {
      setWorkflowId(sourceInfo.workflowId);
    } else if (tabName !== 'workflow-builder') {
      // Clear workflowId when navigating away from workflow builder
      setWorkflowId(null);
    }
    
    setActiveTab(tabName);
  };

  // Handle back navigation
  const handleBack = () => {
    // Always hide sidebar when navigating back (both mobile and desktop)
    setIsSidebarOpen(false);
    
    // If coming from workflow-builder, go back to scenarios page (orchestra-scenarios)
    // Otherwise, go to previous tab or default to 'main'
    if (activeTab === 'workflow-builder') {
      setActiveTab('orchestra-scenarios');
    } else if (previousTab) {
      setActiveTab(previousTab);
      setPreviousTab(null); // Clear previous tab after using it
    } else {
      setActiveTab('main');
    }
  };

  // Handle user login and fetch developer data if needed
  const handleUserLogin = async (user) => {
    try {
      // Normalize user role
      const normalizedRole = normalizeRole(user.role || user.user?.role);
      
      // If user is a developer OR admin, fetch developer data (non-blocking)
      // Admin users are treated as admin developers with full access
      if (isDeveloperRole(normalizedRole) || isAdminRole(normalizedRole)) {
        try {
          const developerData = await a2aDeveloperService.getDeveloperProfile({
            user_id: user.id || user.user?.id
          });
          
          // Merge developer data into user object
          if (developerData && !developerData.error) {
            user.developer_id = developerData.developer_id || developerData.id;
            user.total_apps = developerData.total_apps || 0;
            user.published_apps = developerData.published_apps || 0;
            user.total_downloads = developerData.total_downloads || 0;
            user.total_revenue = developerData.total_revenue || 0;
            user.apps = developerData.apps || [];
            user.api_keys = developerData.api_keys || [];
          }
        } catch (error) {
          // For admin users, use Supabase fallback service
          if (isAdminRole(normalizedRole)) {
            console.warn('A2A authentication failed for admin. Using Supabase fallback with full access.');
            
            try {
              // Use admin developer service to get data from Supabase
              const adminDevData = await getAdminDeveloperProfile(user.id || user.user?.id);
              
              // Merge admin developer data
              user.developer_id = adminDevData.developer_id || adminDevData.id;
              user.total_apps = adminDevData.total_apps || 0;
              user.published_apps = adminDevData.published_apps || 0;
              user.total_downloads = adminDevData.total_downloads || 0;
              user.total_revenue = adminDevData.total_revenue || 0;
              user.apps = adminDevData.apps || [];
              user.api_keys = adminDevData.api_keys || [];
              user.is_admin = true;
              user.has_full_access = true;
              user.verified = adminDevData.verified || true;
              user.company_name = adminDevData.company_name || 'System Administrator';
              
              console.log('[App] Admin developer profile loaded from Supabase:', {
                total_apps: user.total_apps,
                published_apps: user.published_apps,
                has_full_access: user.has_full_access
              });
            } catch (fallbackError) {
              console.warn('Supabase fallback also failed for admin:', fallbackError);
              // Set default admin values
              user.developer_id = user.id || user.user?.id;
              user.total_apps = 0;
              user.published_apps = 0;
              user.total_downloads = 0;
              user.total_revenue = 0;
              user.apps = [];
              user.api_keys = [];
              user.is_admin = true;
              user.has_full_access = true;
            }
          } else {
            // For regular developers, just log the error
            if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
              console.warn('A2A authentication failed (401). Developer profile features may be limited. Check REACT_APP_A2A_SECRET_KEY configuration.');
            } else {
              console.error('Error fetching developer profile:', error);
            }
            // Set default values for regular developers
            user.total_apps = 0;
            user.published_apps = 0;
            user.total_downloads = 0;
            user.total_revenue = 0;
            user.apps = [];
            user.api_keys = [];
          }
        }
      }
      
      // Set normalized role
      if (user.user) {
        user.user.role = normalizedRole;
      } else {
        user.role = normalizedRole;
      }
      
      setCurrentUser(user);
    } catch (error) {
      console.error('Error handling user login:', error);
      // Still set user even if there's an error
      setCurrentUser(user);
    }
  };

  // Render main content based on active tab
  const renderMainContent = () => {
    switch (activeTab) {
      case 'main':
        return (
          <MainPage
            currentUser={currentUser}
            onTabChange={handleTabChange}
          />
        );
      
      // App Management
      case 'my-apps':
        return (
          <div style={{ padding: '20px' }}>
            <h2>📱 我的應用</h2>
            <p>我的應用模組 - 待實現</p>
            <button onClick={handleBack}>返回</button>
          </div>
        );

      case 'create-app':
        return (
          <div style={{ padding: '20px' }}>
            <h2>➕ 建立應用</h2>
            <p>建立應用模組 - 待實現</p>
            <button onClick={handleBack}>返回</button>
          </div>
        );

      case 'app-store':
        return (
          <EnhancedAppStore
            currentTenant={currentUser?.tenant_id || "tn_01"}
            currentUser={currentUser}
            developerMode={true}
          />
        );

      case 'app-details':
        return (
          <AppDetails
            appId={selectedAppId}
            currentTenant={currentUser?.tenant_id || "tn_01"}
            currentUser={currentUser}
            onBack={() => setActiveTab('my-apps')}
            developerMode={true}
          />
        );

      // API Management
      case 'api-keys':
        return (
          <APIKeysModule
            currentUser={currentUser}
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );

      case 'api-docs':
        return (
          <div style={{ padding: '20px' }}>
            <h2>📚 API 文檔</h2>
            <p>API 文檔模組 - 待實現</p>
            <button onClick={handleBack}>返回</button>
          </div>
        );

      case 'api-playground':
        return (
          <APIPlaygroundModule
            currentUser={currentUser}
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );

      case 'webhooks':
        return (
          <div style={{ padding: '20px' }}>
            <h2>🔗 Webhooks</h2>
            <p>Webhooks 管理模組 - 待實現</p>
            <button onClick={handleBack}>返回</button>
          </div>
        );

      // Analytics
      case 'app-analytics':
        return (
          <AppAnalyticsModule
            currentUser={currentUser}
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );

      case 'api-analytics':
        return (
          <div style={{ padding: '20px' }}>
            <h2>📊 API 使用</h2>
            <p>API 使用分析模組 - 待實現</p>
            <button onClick={handleBack}>返回</button>
          </div>
        );

      // Revenue
      case 'revenue':
        return (
          <div style={{ padding: '20px' }}>
            <h2>💵 收益總覽</h2>
            <p>收益總覽模組 - 待實現</p>
            <button onClick={handleBack}>返回</button>
          </div>
        );

      case 'payouts':
        return (
          <div style={{ padding: '20px' }}>
            <h2>💸 提現記錄</h2>
            <p>提現記錄模組 - 待實現</p>
            <button onClick={handleBack}>返回</button>
          </div>
        );

      case 'billing':
        return (
          <BillingDashboard
            currentTenant={currentUser?.tenant_id || "tn_01"}
            onBack={handleBack}
            onTabChange={handleTabChange}
            developerMode={true}
          />
        );

      // Resources
      case 'documentation':
        return (
          <div style={{ padding: '20px' }}>
            <h2>📖 文檔</h2>
            <p>文檔模組 - 待實現</p>
            <button onClick={handleBack}>返回</button>
          </div>
        );

      case 'sdk':
        return (
          <div style={{ padding: '20px' }}>
            <h2>🛠️ SDK</h2>
            <p>SDK 模組 - 待實現</p>
            <button onClick={handleBack}>返回</button>
          </div>
        );

      case 'support':
        return (
          <div style={{ padding: '20px' }}>
            <h2>💬 支援</h2>
            <p>支援模組 - 待實現</p>
            <button onClick={handleBack}>返回</button>
          </div>
        );

      // Testing
      case 'test-environment':
        return (
          <div style={{ padding: '20px' }}>
            <h2>🧪 測試環境</h2>
            <p>測試環境模組 - 待實現</p>
            <button onClick={handleBack}>返回</button>
          </div>
        );

      case 'debugging':
        return (
          <div style={{ padding: '20px' }}>
            <h2>🐛 除錯工具</h2>
            <p>除錯工具模組 - 待實現</p>
            <button onClick={handleBack}>返回</button>
          </div>
        );

      // Security
      case 'security':
        return (
          <div style={{ padding: '20px' }}>
            <h2>🔐 安全設定</h2>
            <p>安全設定模組 - 待實現</p>
            <button onClick={handleBack}>返回</button>
          </div>
        );

      // Profile
      case 'developer-portal':
      case 'developer-profile':
        return (
          <DeveloperPortal
            currentTenant={currentUser?.tenant_id || "tn_01"}
            currentUser={currentUser}
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );

      // Keep some reference/testing modules
      case 'e-notices':
        return (
          <ENoticeModule
            currentUser={currentUser}
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );

      case 'library':
        return (
          <LibraryModule
            currentUser={currentUser}
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );

      case 'group-messages':
        return (
          <GroupMessagesModule currentUser={currentUser} />
        );

      case 'admin-review':
        return (
          <AdminReviewSystem
            currentTenant={currentUser?.tenant_id || "tn_01"}
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );

      // Orchestra AI Platform
      case 'orchestra-workspace':
        return (
          <OrchestraWorkspace
            currentUser={currentUser}
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );

      case 'orchestra-scenarios':
        return (
          <OrchestraScenarios
            currentUser={currentUser}
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );

      case 'workflow-builder':
        return (
          <OrchestraWorkflowBuilder
            currentUser={currentUser}
            onBack={handleBack}
            onTabChange={handleTabChange}
            workflowId={workflowId}
          />
        );

      case 'orchestra-toolbox':
        return (
          <OrchestraToolbox
            currentUser={currentUser}
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );
      
      default:
        return (
          <MainPage
            currentUser={currentUser}
            onTabChange={handleTabChange}
          />
        );
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner"></div>
        <p>Loading application...</p>
      </div>
    );
  }

  // Show authentication if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="auth-container">
        <SupabaseAuth
          onLogin={async (user) => {
            setIsAuthenticated(true);
            await handleUserLogin(user);
          }}
        />
        <AuthSim
          onLogin={async (user) => {
            setIsAuthenticated(true);
            await handleUserLogin(user);
          }}
        />
      </div>
    );
  }

  // Main application render
  return (
    <div className="App">
      {/* Header */}
      <header className="app-header">
        <div className="header-content">
          <button 
            className={`sidebar-toggle ${isSidebarOpen ? 'active' : ''}`}
            onClick={handleSidebarToggle}
            aria-label="Toggle sidebar"
            title={isSidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
          >
            {isSidebarOpen ? '✕' : '☰'}
          </button>
          <h1>MyClassDeveloper</h1>
          <div className="user-info">
            <span>Welcome, {currentUser?.name}</span>
            <button 
              className="logout-btn"
              onClick={() => setIsAuthenticated(false)}
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Backdrop */}
      {isSidebarOpen && isMobile && (
        <div 
          className="sidebar-backdrop"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsSidebarOpen(false);
          }}
        />
      )}

      {/* Sidebar Navigation */}
      <div 
        className={`app-sidebar ${isSidebarOpen ? 'open' : 'closed'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <nav className="sidebar-navigation">
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'main' ? 'active' : ''}`}
              onClick={() => handleTabChange('main')}
            >
              🏠 主頁
            </button>
          </div>
          
          {/* App Management */}
          <div className="sidebar-nav-item sidebar-section-header">
            <span>📱 應用管理</span>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'my-apps' ? 'active' : ''}`}
              onClick={() => handleTabChange('my-apps')}
            >
              📱 我的應用
            </button>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'create-app' ? 'active' : ''}`}
              onClick={() => handleTabChange('create-app')}
            >
              ➕ 建立應用
            </button>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'app-store' ? 'active' : ''}`}
              onClick={() => handleTabChange('app-store')}
            >
              🏪 應用商店
            </button>
          </div>
          
          {/* API Management */}
          <div className="sidebar-nav-item sidebar-section-header">
            <span>🔌 API 管理</span>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'api-keys' ? 'active' : ''}`}
              onClick={() => handleTabChange('api-keys')}
            >
              🔑 API 金鑰
            </button>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'api-docs' ? 'active' : ''}`}
              onClick={() => handleTabChange('api-docs')}
            >
              📚 API 文檔
            </button>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'api-playground' ? 'active' : ''}`}
              onClick={() => handleTabChange('api-playground')}
            >
              🧪 API 測試
            </button>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'webhooks' ? 'active' : ''}`}
              onClick={() => handleTabChange('webhooks')}
            >
              🔗 Webhooks
            </button>
          </div>
          
          {/* Analytics */}
          <div className="sidebar-nav-item sidebar-section-header">
            <span>📊 分析</span>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'app-analytics' ? 'active' : ''}`}
              onClick={() => handleTabChange('app-analytics')}
            >
              📈 應用分析
            </button>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'api-analytics' ? 'active' : ''}`}
              onClick={() => handleTabChange('api-analytics')}
            >
              📊 API 使用
            </button>
          </div>
          
          {/* Revenue */}
          <div className="sidebar-nav-item sidebar-section-header">
            <span>💰 收益</span>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'revenue' ? 'active' : ''}`}
              onClick={() => handleTabChange('revenue')}
            >
              💵 收益總覽
            </button>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'payouts' ? 'active' : ''}`}
              onClick={() => handleTabChange('payouts')}
            >
              💸 提現記錄
            </button>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'billing' ? 'active' : ''}`}
              onClick={() => handleTabChange('billing')}
            >
              💳 帳單設定
            </button>
          </div>
          
          {/* Resources */}
          <div className="sidebar-nav-item sidebar-section-header">
            <span>📚 資源</span>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'documentation' ? 'active' : ''}`}
              onClick={() => handleTabChange('documentation')}
            >
              📖 文檔
            </button>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'sdk' ? 'active' : ''}`}
              onClick={() => handleTabChange('sdk')}
            >
              🛠️ SDK
            </button>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'support' ? 'active' : ''}`}
              onClick={() => handleTabChange('support')}
            >
              💬 支援
            </button>
          </div>
          
          {/* Testing */}
          <div className="sidebar-nav-item sidebar-section-header">
            <span>🧪 測試</span>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'test-environment' ? 'active' : ''}`}
              onClick={() => handleTabChange('test-environment')}
            >
              🧪 測試環境
            </button>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'debugging' ? 'active' : ''}`}
              onClick={() => handleTabChange('debugging')}
            >
              🐛 除錯工具
            </button>
          </div>
          
          {/* Security */}
          <div className="sidebar-nav-item sidebar-section-header">
            <span>🔒 安全</span>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'security' ? 'active' : ''}`}
              onClick={() => handleTabChange('security')}
            >
              🔐 安全設定
            </button>
          </div>
          
          {/* Profile */}
          <div className="sidebar-nav-item sidebar-section-header">
            <span>👤 帳戶</span>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'developer-profile' ? 'active' : ''}`}
              onClick={() => handleTabChange('developer-profile')}
            >
              👨‍💻 開發者資料
            </button>
          </div>

          {/* Orchestra AI Platform */}
          <div className="sidebar-nav-item sidebar-section-header">
            <span>🎼 Orchestra AI Platform</span>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'orchestra-workspace' ? 'active' : ''}`}
              onClick={() => handleTabChange('orchestra-workspace')}
            >
              📁 開發者工作區
            </button>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'orchestra-scenarios' ? 'active' : ''}`}
              onClick={() => handleTabChange('orchestra-scenarios')}
            >
              🎓 教育場景範本 (10個範例)
            </button>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'workflow-builder' ? 'active' : ''}`}
              onClick={() => handleTabChange('workflow-builder')}
            >
              🔧 視覺化工作流程建構器
            </button>
          </div>
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'orchestra-toolbox' ? 'active' : ''}`}
              onClick={() => handleTabChange('orchestra-toolbox')}
            >
              🧰 API 工具箱
            </button>
          </div>
        </nav>
      </div>

      {/* Main Content Area */}
      <main className={`app-main ${isSidebarOpen ? 'with-sidebar' : 'without-sidebar'}`}>
        {renderMainContent()}
      </main>

      {/* Footer */}
      <footer className="app-footer">
        <CreditsPanel />
      </footer>
    </div>
  );
}

export default App;
